import React, { useState } from 'react'
import { toast } from 'react-toastify'
import AccessTimeIcon from '@mui/icons-material/AccessTime'
import CreditCardIcon from '@mui/icons-material/CreditCard'
import ExpandLessIcon from '@mui/icons-material/ExpandLess'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import FiberManualRecordIcon from '@mui/icons-material/FiberManualRecord'
import Inventory2OutlinedIcon from '@mui/icons-material/Inventory2Outlined'
import PriceChangeOutlinedIcon from '@mui/icons-material/PriceChangeOutlined'
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong'
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  IconButton,
  Stack,
  TextField,
  Typography
} from '@mui/material'
import { useQuery } from '@tanstack/react-query'
import FinanceAPI from 'api/ops/v2/FinanceAPI'
import format from 'date-fns/format'
import formatDistanceToNow from 'date-fns/formatDistanceToNow'
import OrbitSession from 'hooks/useCookies'
import useOrbitFlag from 'hooks/useOrbitFlag'
import {
  OrderPaymentRow,
  RefundRecord
} from 'types/api/ops/v2/finance.type'

import { ALLOWED_ROLES, REFUND_ROLE_PERCENT } from '../constant'

const STATUS_CONFIG: Record<
  string,
  { color: string; bg: string; border: string }
> = {
  completed: { color: '#166534', bg: '#DCFCE7', border: '#22C55E' },
  processed: { color: '#166534', bg: '#DCFCE7', border: '#22C55E' },
  succeeded: { color: '#166534', bg: '#DCFCE7', border: '#22C55E' },
  paid: { color: '#166534', bg: '#DCFCE7', border: '#22C55E' },
  pending: { color: '#B45309', bg: '#FFF4DC', border: '#F59E0B' },
  processing: { color: '#B45309', bg: '#FFF4DC', border: '#F59E0B' },
  failed: { color: '#B91C1C', bg: '#FEE2E2', border: '#EF4444' },
  expired: { color: '#4B5563', bg: '#F3F4F6', border: '#9CA3AF' },
  refunded: { color: '#5B21B6', bg: '#EDE9FE', border: '#8B5CF6' },
  success: { color: '#166534', bg: '#DCFCE7', border: '#22C55E' },
  'partially refunded': {
    color: '#5B21B6',
    bg: '#EDE9FE',
    border: '#8B5CF6'
  },
  'fully refunded': { color: '#166534', bg: '#DCFCE7', border: '#22C55E' },
  cancelled: { color: '#4B5563', bg: '#F3F4F6', border: '#9CA3AF' }
}

const DEFAULT_STATUS = {
  color: '#253140',
  bg: '#EEF2F7',
  border: '#94A3B8'
}

const getStatusStyle = (status: string) => {
  const lower = status?.toLowerCase() || ''
  return STATUS_CONFIG[lower] || DEFAULT_STATUS
}

const getStatusLabel = (status: string) => {
  const lower = status?.toLowerCase() || ''
  if (lower === 'completed') return 'PROCESSED'
  return status?.toUpperCase() || '-'
}

const formatDate = (dateStr?: string) => {
  if (!dateStr) return '-'
  try {
    return format(new Date(dateStr), 'MMM dd, yyyy HH:mm') + ' UTC'
  } catch {
    return dateStr
  }
}

const timeAgo = (dateStr: string) => {
  try {
    return formatDistanceToNow(new Date(dateStr), { addSuffix: true })
  } catch {
    return ''
  }
}

interface OrderPaymentCardProps {
  order: OrderPaymentRow
}

function OrderPaymentCard({ order }: OrderPaymentCardProps) {
  const [expanded, setExpanded] = useState(false)
  const [refundOpen, setRefundOpen] = useState(false)
  const [refundAmount, setRefundAmount] = useState('')
  const [refundReason, setRefundReason] = useState('')
  const [ticketId, setTicketId] = useState('')
  const [refundLoading, setRefundLoading] = useState(false)

  const faberAdminRole = OrbitSession.get('role')
  const faberRefundFlag = useOrbitFlag('refund-v2')
  const maxRefundPercent = REFUND_ROLE_PERCENT[faberAdminRole] ?? 0
  const canRefund =
    [ALLOWED_ROLES.OPS_OWNER, ALLOWED_ROLES.OPS].includes(faberAdminRole) &&
    faberRefundFlag

  const payment = order.payment
  const primaryItem = order.items?.[0]
  const productLabel = primaryItem?.product_name || order.order_type || 'Order'
  const attempt = payment?.attempts?.[payment.attempts.length - 1]
  const orderStyle = getStatusStyle(order.order_status)
  const itemCount = order.items?.length ?? 0
  const orderGrossAmount = getGrossAmount(order.net_amount, order.discount)
  const {
    data: orderRefunds,
    isLoading: refundsLoading,
    refetch: refetchOrderRefunds
  } = useQuery(
    ['getOrderRefundsV2', order.order_id],
    async () => FinanceAPI.getOrderRefunds(order.order_id),
    {
      enabled: expanded && !!order.order_id
    }
  )
  const refunds = orderRefunds?.data?.data ?? []

  const isRefundable =
    canRefund &&
    payment &&
    !['refunded', 'failed', 'expired', 'cancelled'].includes(
      order.order_status?.toLowerCase()
    )

  // net_amount is the actual charged amount after discount (in dollars)
  const netAmountCents = Math.round(order.net_amount * 100)
  const maxRefundAmount = Math.floor((netAmountCents * maxRefundPercent) / 100)

  const handleRefundSubmit = async () => {
    if (!payment || !canRefund) return
    setRefundLoading(true)
    try {
      const amount = refundAmount
        ? Math.round(parseFloat(refundAmount) * 100)
        : netAmountCents

      if (amount > maxRefundAmount) {
        toast.error(
          `Your role allows refunding up to ${maxRefundPercent}% ($${(
            maxRefundAmount / 100
          ).toFixed(2)})`
        )
        setRefundLoading(false)
        return
      }

      if (amount > netAmountCents) {
        toast.error(
          `Refund cannot exceed net amount after discount ($${order.net_amount.toFixed(
            2
          )})`
        )
        setRefundLoading(false)
        return
      }

      await FinanceAPI.submitRefund({
        order_id: order.order_id,
        payment_id: payment.payment_id,
        user_id: order.user_id,
        refund_amount: amount,
        refund_reason: refundReason,
        ticket_id: ticketId,
        currency: payment.currency,
        terminate_plan: false,
        created_by: faberAdminRole
      })
      toast.success('Refund submitted successfully')
      setRefundOpen(false)
      setRefundAmount('')
      setRefundReason('')
      setTicketId('')
      if (expanded) {
        refetchOrderRefunds()
      }
    } catch (err: unknown) {
      toast.error(getRefundErrorMessage(err))
    } finally {
      setRefundLoading(false)
    }
  }

  return (
    <Card
      sx={{
        border: '1px solid #DFE6EF',
        borderLeft: `4px solid ${orderStyle.border}`,
        borderRadius: 2,
        boxShadow: '0 1px 2px rgba(15, 23, 42, 0.04)',
        overflow: 'hidden',
        transitionProperty: 'border-color, background-color, box-shadow',
        transitionDuration: '160ms',
        transitionTimingFunction: 'ease-out',
        '&:hover': {
          bgcolor: '#FCFDFE',
          borderColor: '#B8C5D6',
          boxShadow: '0 10px 22px rgba(15, 23, 42, 0.07)'
        }
      }}
    >
      <CardContent
        sx={{
          p: { xs: 2, md: 2.5 },
          '&:last-child': { pb: 2.5 },
          cursor: 'pointer'
        }}
        onClick={(e) => {
          // Don't toggle if clicking on the Refund button or dialog inputs
          if ((e.target as HTMLElement).closest('button, a, input, textarea'))
            return
          setExpanded(!expanded)
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'flex-start',
            justifyContent: 'space-between',
            gap: 2,
            flexWrap: 'wrap'
          }}
        >
          <Box sx={{ minWidth: 0, flex: '1 1 420px' }}>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                mb: 1,
                flexWrap: 'wrap'
              }}
            >
              <Chip
                label={getStatusLabel(order.order_status)}
                size='small'
                sx={{
                  height: 30,
                  borderRadius: 15,
                  color: orderStyle.color,
                  bgcolor: orderStyle.bg,
                  fontWeight: 800,
                  fontSize: '0.72rem'
                }}
              />
              <Typography sx={monoIdStyle}>#{order.order_id}</Typography>
              {payment && (
                <Typography sx={monoIdStyle}>#{payment.payment_id}</Typography>
              )}
            </Box>

            <Typography
              title={productLabel}
              sx={{
                color: '#172033',
                fontSize: '1.05rem',
                fontWeight: 800,
                lineHeight: 1.25,
                mb: 1,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap'
              }}
            >
              {productLabel}
            </Typography>

            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                color: '#667085',
                flexWrap: 'wrap'
              }}
            >
              <InfoInline
                icon={<ReceiptLongIcon sx={{ fontSize: 16 }} />}
                value={order.order_type || 'Order'}
              />
              <InfoInline
                icon={<Inventory2OutlinedIcon sx={{ fontSize: 16 }} />}
                value={`${itemCount} item${itemCount === 1 ? '' : 's'}`}
              />
              <InfoInline
                icon={<AccessTimeIcon sx={{ fontSize: 16 }} />}
                value={timeAgo(order.created_at)}
              />
            </Box>
          </Box>

          <Box
            sx={{
              display: 'flex',
              alignItems: 'flex-start',
              justifyContent: 'flex-end',
              gap: 1,
              flex: '0 0 auto',
              ml: 'auto'
            }}
          >
            <Box sx={{ textAlign: 'right', minWidth: 110 }}>
              <Typography
                sx={{
                  color: '#667085',
                  fontSize: '0.72rem',
                  fontWeight: 800,
                  letterSpacing: 0,
                  textTransform: 'uppercase'
                }}
              >
                Net
              </Typography>
              <Typography
                sx={{
                  color: '#172033',
                  fontSize: '1.35rem',
                  fontWeight: 900,
                  lineHeight: 1.15,
                  fontVariantNumeric: 'tabular-nums'
                }}
              >
                {formatAmount(order.net_amount)}
              </Typography>
            </Box>

            <Button
              size='small'
              variant='outlined'
              color='error'
              disabled={!isRefundable}
              startIcon={<PriceChangeOutlinedIcon />}
              onClick={() => setRefundOpen(true)}
              sx={{
                textTransform: 'none',
                fontWeight: 700,
                fontSize: '0.78rem',
                borderRadius: 1.5,
                px: 1.5,
                whiteSpace: 'nowrap'
              }}
            >
              Refund
            </Button>

            <IconButton
              size='small'
              aria-label={
                expanded ? 'Collapse order details' : 'Expand order details'
              }
              onClick={() => setExpanded(!expanded)}
              sx={iconButtonStyle}
            >
              {expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            </IconButton>
          </Box>
        </Box>

        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: {
              xs: '1fr',
              sm: 'repeat(2, minmax(0, 1fr))',
              lg: 'repeat(4, minmax(0, 1fr))'
            },
            gap: 1,
            mt: 2
          }}
        >
          <SummaryTile label='Payment' value={getPaymentLabel(payment)} />
          <SummaryTile label='Promo' value={order.promocode || '-'} />
          <SummaryTile label='Placed' value={formatDate(order.placed_at)} />
          <SummaryTile label='Paid' value={formatDate(order.paid_at)} />
        </Box>
      </CardContent>

      <Collapse in={expanded}>
        <Divider />
        <Box sx={{ bgcolor: '#F8FAFC', p: { xs: 2, md: 2.5 } }}>
          <Grid container spacing={2}>
            <Grid item xs={12} lg={7}>
              <SectionTitle title='Order Details' />

              {order.items.length > 0 ? (
                <Stack spacing={1}>
                  {order.items.map((item) => (
                    <Box key={item.id} sx={lineItemStyle}>
                      <Box sx={{ minWidth: 0 }}>
                        <Typography
                          sx={{
                            color: '#172033',
                            fontSize: '0.88rem',
                            fontWeight: 800,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap'
                          }}
                        >
                          {item.product_name}
                        </Typography>
                        <Typography sx={mutedTextStyle}>
                          {item.product_type || '-'} - Qty {item.quantity}
                        </Typography>
                      </Box>

                      <Box
                        sx={{
                          display: 'grid',
                          gridTemplateColumns: {
                            xs: 'repeat(2, minmax(92px, 1fr))',
                            sm: 'repeat(4, minmax(76px, 1fr))'
                          },
                          gap: 1,
                          minWidth: { xs: '100%', sm: 360 },
                          textAlign: 'right'
                        }}
                      >
                        <MiniAmount
                          label='Gross'
                          value={getGrossAmount(item.net_amount, item.discount)}
                        />
                        <MiniAmount label='Tax' value={item.tax_amount} />
                        <MiniAmount
                          label='Disc'
                          value={formatDiscount(item.discount)}
                        />
                        <MiniAmount label='Net' value={item.net_amount} />
                      </Box>
                    </Box>
                  ))}
                </Stack>
              ) : (
                <Typography sx={mutedTextStyle}>
                  No order items found
                </Typography>
              )}

              <Box sx={{ ...detailPanelStyle, mt: 1.5 }}>
                <DetailRow label='Order Type' value={order.order_type || '-'} />
                <DetailRow
                  label='Placed At'
                  value={formatDate(order.placed_at)}
                />
                <DetailRow label='Paid At' value={formatDate(order.paid_at)} />
              </Box>
            </Grid>

            <Grid item xs={12} lg={5}>
              <SectionTitle title='Payment Information' />

              {payment ? (
                <Stack spacing={1.5}>
                  <Box sx={detailPanelStyle}>
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                        mb: 1
                      }}
                    >
                      <CreditCardIcon sx={{ color: '#2F6FED', fontSize: 18 }} />
                      <Typography
                        sx={{
                          color: '#172033',
                          fontSize: '0.88rem',
                          fontWeight: 800
                        }}
                      >
                        {getPaymentMethodLabel(attempt)}
                      </Typography>
                    </Box>
                    <DetailRow label='PSP' value={capitalize(attempt?.psp)} />
                    <DetailRow label='Tenant' value={payment.tenant || '-'} />
                    <DetailRow
                      label='Receipt'
                      value={
                        payment.receipt_number
                          ? `#${payment.receipt_number}`
                          : '-'
                      }
                    />
                    <DetailRow
                      label='3DS'
                      value={attempt?.is_3ds ? 'Authenticated' : '-'}
                    />
                  </Box>

                  <Box sx={detailPanelStyle}>
                    <AmountRow
                      label='Gross'
                      value={formatAmount(orderGrossAmount)}
                      bold
                    />
                    <AmountRow
                      label={`Tax${
                        payment.tax_country ? ` (${payment.tax_country})` : ''
                      }`}
                      value={formatAmount(order.tax)}
                    />
                    <AmountRow
                      label='Discount'
                      value={formatDiscount(order.discount)}
                    />
                    <Divider sx={{ my: 0.75 }} />
                    <AmountRow
                      label='Net'
                      value={formatAmount(order.net_amount)}
                    />
                  </Box>

                  {payment.attempts.length > 0 && (
                    <Box sx={detailPanelStyle}>
                      <Typography
                        sx={{
                          color: '#667085',
                          fontSize: '0.72rem',
                          fontWeight: 800,
                          letterSpacing: 0,
                          textTransform: 'uppercase',
                          mb: 1
                        }}
                      >
                        Attempt Timeline
                      </Typography>
                      <Stack spacing={1}>
                        {[...payment.attempts].reverse().map((att) => {
                          const attStyle = getStatusStyle(att.status)
                          return (
                            <Box
                              key={att.id}
                              sx={{
                                display: 'flex',
                                alignItems: 'flex-start',
                                gap: 1
                              }}
                            >
                              <FiberManualRecordIcon
                                sx={{
                                  fontSize: 10,
                                  mt: 0.55,
                                  color: attStyle.border
                                }}
                              />
                              <Box>
                                <Typography
                                  sx={{
                                    color: '#172033',
                                    fontSize: '0.8rem',
                                    fontWeight: 700
                                  }}
                                >
                                  {getAttemptLabel(att.status)}
                                </Typography>
                                <Typography sx={mutedTextStyle}>
                                  {formatDate(att.created_at)}
                                </Typography>
                              </Box>
                            </Box>
                          )
                        })}
                      </Stack>
                    </Box>
                  )}

                  <RefundHistoryPanel
                    currency={payment.currency || order.currency}
                    isLoading={refundsLoading}
                    refunds={refunds}
                  />
                </Stack>
              ) : (
                <Box sx={detailPanelStyle}>
                  <Typography sx={mutedTextStyle}>
                    No payment recorded
                  </Typography>
                </Box>
              )}
            </Grid>
          </Grid>
        </Box>
      </Collapse>

      {/* Refund Dialog */}
      <Dialog
        open={refundOpen}
        onClose={() => setRefundOpen(false)}
        maxWidth='sm'
        fullWidth
      >
        <DialogTitle sx={{ fontWeight: 700 }}>Refund Order</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <Box sx={{ display: 'flex', gap: 2 }}>
              <Typography sx={{ fontSize: '0.85rem', color: '#667085' }}>
                Order:
              </Typography>
              <Typography sx={{ fontSize: '0.85rem', fontWeight: 700 }}>
                #{order.order_id}
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', gap: 2 }}>
              <Typography sx={{ fontSize: '0.85rem', color: '#667085' }}>
                Net Paid (after discount):
              </Typography>
              <Typography sx={{ fontSize: '0.85rem', fontWeight: 700 }}>
                {order.currency} {order.net_amount.toFixed(2)}
              </Typography>
            </Box>
            <TextField
              label={`Refund Amount (max $${(maxRefundAmount / 100).toFixed(
                2
              )})`}
              type='number'
              size='small'
              value={refundAmount}
              onChange={(e) => setRefundAmount(e.target.value)}
              placeholder={order.net_amount.toFixed(2)}
              helperText='Leave blank for full refund'
              error={
                !!refundAmount &&
                Math.round(parseFloat(refundAmount) * 100) > netAmountCents
              }
              fullWidth
            />
            <TextField
              label='Refund Reason'
              size='small'
              value={refundReason}
              onChange={(e) => setRefundReason(e.target.value)}
              required
              fullWidth
              multiline
              rows={2}
            />
            <TextField
              label='Ticket ID'
              size='small'
              value={ticketId}
              onChange={(e) => setTicketId(e.target.value)}
              required
              fullWidth
            />
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setRefundOpen(false)} disabled={refundLoading}>
            Cancel
          </Button>
          <Button
            variant='contained'
            color='error'
            onClick={handleRefundSubmit}
            disabled={
              refundLoading ||
              !refundReason ||
              !ticketId ||
              (!!refundAmount &&
                Math.round(parseFloat(refundAmount) * 100) > netAmountCents)
            }
          >
            {refundLoading ? 'Submitting...' : 'Submit Refund'}
          </Button>
        </DialogActions>
      </Dialog>
    </Card>
  )
}

function RefundHistoryPanel({
  currency,
  isLoading,
  refunds
}: {
  currency: string
  isLoading: boolean
  refunds: RefundRecord[]
}) {
  return (
    <Box sx={detailPanelStyle}>
      <Typography
        sx={{
          color: '#667085',
          fontSize: '0.72rem',
          fontWeight: 800,
          letterSpacing: 0,
          textTransform: 'uppercase',
          mb: 1
        }}
      >
        Refund History
      </Typography>

      {isLoading ? (
        <Typography sx={mutedTextStyle}>Loading refunds...</Typography>
      ) : refunds.length === 0 ? (
        <Typography sx={mutedTextStyle}>No refunds found</Typography>
      ) : (
        <Stack spacing={1}>
          {refunds.map((refund) => {
            const refundStyle = getStatusStyle(refund.refund_status)
            const amount = refund.refund_amount || refund.amount || 0
            return (
              <Box
                key={refund.id}
                sx={{
                  border: '1px solid #EDF0F5',
                  borderLeft: `3px solid ${refundStyle.border}`,
                  borderRadius: 1.5,
                  bgcolor: '#fff',
                  p: 1
                }}
              >
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'flex-start',
                    gap: 1
                  }}
                >
                  <Box sx={{ minWidth: 0 }}>
                    <Typography
                      sx={{
                        color: '#172033',
                        fontSize: '0.78rem',
                        fontWeight: 800,
                        overflowWrap: 'anywhere'
                      }}
                    >
                      {refund.refund_reason || 'No reason provided'}
                    </Typography>
                    <Typography sx={mutedTextStyle}>
                      Ticket: {refund.ticket_id || '-'} |{' '}
                      {formatDate(refund.created_at)}
                    </Typography>
                    <Typography sx={mutedTextStyle}>
                      Refund ID: {refund.refund_payment_id || refund.id}
                    </Typography>
                  </Box>

                  <Box sx={{ textAlign: 'right', flex: '0 0 auto' }}>
                    <Typography
                      sx={{
                        color: '#172033',
                        fontSize: '0.84rem',
                        fontWeight: 900,
                        fontVariantNumeric: 'tabular-nums'
                      }}
                    >
                      {formatCentsAmount(amount, refund.currency || currency)}
                    </Typography>
                    <Chip
                      label={refund.refund_status || '-'}
                      size='small'
                      sx={{
                        height: 22,
                        borderRadius: 11,
                        color: refundStyle.color,
                        bgcolor: refundStyle.bg,
                        fontWeight: 800,
                        fontSize: '0.65rem',
                        mt: 0.5
                      }}
                    />
                  </Box>
                </Box>
              </Box>
            )
          })}
        </Stack>
      )}
    </Box>
  )
}

function InfoInline({ icon, value }: { icon: React.ReactNode; value: string }) {
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
      {icon}
      <Typography sx={{ fontSize: '0.78rem', fontWeight: 700 }}>
        {value}
      </Typography>
    </Box>
  )
}

function SummaryTile({ label, value }: { label: string; value: string }) {
  return (
    <Box sx={summaryTileStyle}>
      <Typography sx={summaryLabelStyle}>{label}</Typography>
      <Typography title={value} sx={summaryValueStyle}>
        {value}
      </Typography>
    </Box>
  )
}

function SectionTitle({ title }: { title: string }) {
  return (
    <Typography
      sx={{
        color: '#172033',
        fontSize: '0.86rem',
        fontWeight: 900,
        mb: 1
      }}
    >
      {title}
    </Typography>
  )
}

function DetailRow({ label, value }: { label: string; value: string }) {
  return (
    <Box
      sx={{ display: 'flex', justifyContent: 'space-between', gap: 2, py: 0.3 }}
    >
      <Typography sx={mutedTextStyle}>{label}</Typography>
      <Typography
        sx={{
          color: '#172033',
          fontSize: '0.8rem',
          fontWeight: 700,
          textAlign: 'right',
          overflowWrap: 'anywhere'
        }}
      >
        {value}
      </Typography>
    </Box>
  )
}

function AmountRow({
  label,
  value,
  bold
}: {
  label: string
  value: string
  bold?: boolean
}) {
  return (
    <Box sx={{ display: 'flex', justifyContent: 'space-between', py: 0.35 }}>
      <Typography
        sx={{
          color: '#667085',
          fontSize: '0.8rem',
          fontWeight: bold ? 800 : 600
        }}
      >
        {label}
      </Typography>
      <Typography
        sx={{
          color: '#172033',
          fontSize: '0.84rem',
          fontWeight: bold ? 900 : 700,
          fontVariantNumeric: 'tabular-nums'
        }}
      >
        {value}
      </Typography>
    </Box>
  )
}

function MiniAmount({
  label,
  value
}: {
  label: string
  value: number | string
}) {
  return (
    <Box>
      <Typography sx={summaryLabelStyle}>{label}</Typography>
      <Typography
        sx={{
          color: '#172033',
          fontSize: '0.82rem',
          fontWeight: 800,
          fontVariantNumeric: 'tabular-nums'
        }}
      >
        {typeof value === 'number' ? formatAmount(value) : value}
      </Typography>
    </Box>
  )
}

function getPaymentLabel(payment: OrderPaymentRow['payment']) {
  if (!payment) return 'No payment'
  return payment.payment_status
    ? capitalize(payment.payment_status)
    : payment.payment_id || 'Payment recorded'
}

function getPaymentMethodLabel(
  attempt:
    | NonNullable<OrderPaymentRow['payment']>['attempts'][number]
    | undefined
) {
  if (!attempt) return 'Payment method unavailable'
  if (attempt.card_brand) {
    return `${capitalize(attempt.card_brand)} ending in ${
      attempt.last4 || '****'
    }`
  }
  return attempt.payment_method || attempt.wallet_provider || 'Payment method'
}

function formatAmount(amount: number) {
  return `$${amount.toFixed(2)}`
}

function formatCentsAmount(amount: number, currency?: string) {
  return new Intl.NumberFormat(undefined, {
    style: 'currency',
    currency: currency || 'USD'
  }).format(amount / 100)
}

function getRefundErrorMessage(err: unknown) {
  if (
    typeof err === 'object' &&
    err !== null &&
    'response' in err &&
    typeof err.response === 'object' &&
    err.response !== null &&
    'data' in err.response &&
    typeof err.response.data === 'object' &&
    err.response.data !== null &&
    'error' in err.response.data &&
    typeof err.response.data.error === 'string'
  ) {
    return err.response.data.error
  }
  return 'Failed to submit refund'
}

function formatDiscount(amount: number) {
  if (!amount) return '$0.00'
  return `-$${Math.abs(amount).toFixed(2)}`
}

function getGrossAmount(netAmount: number, discount: number) {
  return netAmount + Math.abs(discount)
}

function capitalize(s?: string) {
  if (!s) return '-'
  return s.charAt(0).toUpperCase() + s.slice(1)
}

function getAttemptLabel(status: string) {
  const labels: Record<string, string> = {
    succeeded: 'Payment Succeeded',
    processing: '3DS Challenge Completed',
    processed: 'Payment Processed',
    pending: 'Payment Intent Created',
    failed: 'Payment Failed',
    expired: 'Payment Expired',
    cancelled: 'Payment Cancelled'
  }
  return labels[status?.toLowerCase()] || capitalize(status)
}

const monoIdStyle = {
  color: '#344054',
  bgcolor: '#F8FAFC',
  border: '1px solid #D9E1EC',
  borderRadius: 1,
  px: 1,
  py: 0.45,
  fontFamily: 'monospace',
  fontSize: '0.78rem',
  fontWeight: 800,
  lineHeight: 1
}

const iconButtonStyle = {
  width: 40,
  height: 40,
  color: '#4D5968',
  transitionProperty: 'background-color, color, transform',
  transitionDuration: '140ms',
  transitionTimingFunction: 'ease-out',
  '&:hover': {
    bgcolor: '#EDF3FF',
    color: '#1E5AC8'
  },
  '&:active': {
    transform: 'scale(0.96)'
  }
}

const summaryTileStyle = {
  minWidth: 0,
  bgcolor: '#F8FAFC',
  border: '1px solid #E5EBF3',
  borderRadius: 1.25,
  px: 1.25,
  py: 1
}

const summaryLabelStyle = {
  color: '#667085',
  fontSize: '0.68rem',
  fontWeight: 800,
  letterSpacing: 0,
  textTransform: 'uppercase'
}

const summaryValueStyle = {
  color: '#172033',
  fontSize: '0.82rem',
  fontWeight: 800,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap'
}

const detailPanelStyle = {
  bgcolor: '#fff',
  border: '1px solid #E5EBF3',
  borderRadius: 1.5,
  p: 1.5
}

const lineItemStyle = {
  display: 'flex',
  justifyContent: 'space-between',
  gap: 2,
  bgcolor: '#fff',
  border: '1px solid #E5EBF3',
  borderRadius: 1.5,
  p: 1.5,
  flexWrap: 'wrap'
}

const mutedTextStyle = {
  color: '#667085',
  fontSize: '0.78rem',
  fontWeight: 600
}

export default OrderPaymentCard
